import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Image, Video, Plus, Trash2, X, Calendar, MapPin, Upload
} from 'lucide-react';
import { toast } from 'sonner';

export default function ArtistGallery() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    media_type: 'image',
    url: '',
    caption: '',
    event_name: '',
    event_date: ''
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/artist/gallery');
      setGallery(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Artist profile not found');
        navigate('/become-artist');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, url: response.data.url, media_type: 'image' }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddMedia = async (e) => {
    e.preventDefault();
    if (!formData.url) {
      toast.error('Please upload an image or enter a video URL');
      return;
    }

    try {
      await apiClient.post('/artist/gallery', formData);
      toast.success('Media added to gallery!');
      setFormData({ media_type: 'image', url: '', caption: '', event_name: '', event_date: '' });
      setShowAddForm(false);
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add media');
    }
  };

  const handleDeleteMedia = async (galleryId) => {
    if (!window.confirm('Delete this media?')) return;

    try {
      await apiClient.delete(`/artist/gallery/${galleryId}`);
      setGallery(gallery.filter(g => g.gallery_id !== galleryId));
      toast.success('Media deleted!');
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Image className="w-8 h-8 text-violet-400" />
            <h1 className="text-3xl font-bold text-white">Media Gallery</h1>
          </div>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)} className="bg-violet-600 hover:bg-violet-500">
              <Plus className="w-4 h-4 mr-2" />
              Add Media
            </Button>
          )}
        </div>

        <p className="text-white/60 mb-8">
          Showcase your best moments! Add photos and videos from past events, performances, and behind-the-scenes.
        </p>

        {/* Add Media Form */}
        {showAddForm && (
          <Card className="bg-white/5 border-white/10 mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Add New Media</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddMedia} className="space-y-4">
                {/* Media Type Selection */}
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, media_type: 'image', url: '' })}
                    className={`flex-1 p-4 rounded-xl border transition-all ${
                      formData.media_type === 'image'
                        ? 'bg-violet-600/20 border-violet-500'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Image className="w-8 h-8 mx-auto mb-2 text-violet-400" />
                    <p className="text-white font-medium">Photo</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, media_type: 'video', url: '' })}
                    className={`flex-1 p-4 rounded-xl border transition-all ${
                      formData.media_type === 'video'
                        ? 'bg-violet-600/20 border-violet-500'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Video className="w-8 h-8 mx-auto mb-2 text-violet-400" />
                    <p className="text-white font-medium">Video</p>
                  </button>
                </div>

                {/* Upload/URL Input */}
                {formData.media_type === 'image' ? (
                  <div>
                    <Label className="text-white/80">Upload Photo</Label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-2 border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-violet-500 transition-colors"
                    >
                      {formData.url ? (
                        <img src={formData.url} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 mx-auto mb-4 text-white/40" />
                          <p className="text-white/60">{uploading ? 'Uploading...' : 'Click to upload photo'}</p>
                          <p className="text-sm text-white/40 mt-1">JPG, PNG, WebP up to 5MB</p>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="text-white/80">Video URL (YouTube, Vimeo, etc.)</Label>
                    <Input
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="mt-2 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                )}

                {/* Event Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/80">Event Name (optional)</Label>
                    <Input
                      value={formData.event_name}
                      onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                      placeholder="e.g. Ultra Music Festival"
                      className="mt-2 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Event Date (optional)</Label>
                    <Input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      className="mt-2 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white/80">Caption (optional)</Label>
                  <textarea
                    value={formData.caption}
                    onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                    placeholder="Describe this moment..."
                    rows={2}
                    className="w-full mt-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:border-violet-500 outline-none resize-none"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="bg-violet-600 hover:bg-violet-500">
                    Add to Gallery
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Gallery Grid */}
        {gallery.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Image className="w-16 h-16 mx-auto mb-4 text-white/40" />
              <h3 className="text-xl font-semibold text-white mb-2">No media yet</h3>
              <p className="text-white/60 mb-4">Start building your portfolio by adding photos and videos from your events!</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-violet-600 hover:bg-violet-500">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Media
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gallery.map((item) => (
              <Card key={item.gallery_id} className="bg-white/5 border-white/10 overflow-hidden group">
                <div className="relative aspect-video">
                  {item.media_type === 'image' ? (
                    <img 
                      src={item.url} 
                      alt={item.caption || 'Gallery item'} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-black flex items-center justify-center">
                      <Video className="w-12 h-12 text-white/60" />
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Button variant="outline" size="sm">Watch Video</Button>
                      </a>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteMedia(item.gallery_id)}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <CardContent className="p-4">
                  {item.caption && <p className="text-white mb-2">{item.caption}</p>}
                  <div className="flex items-center gap-4 text-sm text-white/60">
                    {item.event_name && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {item.event_name}
                      </span>
                    )}
                    {item.event_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(item.event_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
